import React from "react";
import StepModel from "../../../model/configurator/StepModel";
import Field from "../../../model/configurator/Field";
import BaseStep from "../../shared/steps/BaseStep";
import formModelStore from "../../../store/FormModelStore";
import ExpandableContainer from "../../configuration/steps/ExpandableContainer";
import APIServicesRegistry from "../../../api-services/APIServicesRegistry";
import TWSCalculationApiService from "../../../api-services/TWSCalculationApiService";
import flattenConfigurationModel from "../../../flattenConfigurationModel";
import TwsMatchingTapWaterSystemField from "./custom-fields/tws-matching-tap-water-systems/TwsMatchingTapWaterSystemField";
import TwsTwsOptionsField from "./custom-fields/tws-tws-options/TwsTwsOptionsField";
import { Calculation, Selection } from "../../../../src/application/Contracts";
import TwsMatchingUnitField from "./custom-fields/tws-matching-units/TwsMatchingUnitField";
import { WaterSystemTechnicalData } from "./water-system/WaterSystemTechnicalData";
import { RenderStepInputReadonlyField } from "./custom-fields/Helper";
import { TwsProductCatalogSelectProduct } from "./custom-fields/tws-product-catalog-select-product/TwsProductCatalogSelectProduct";
import { GetOptionsWeight, IsInstantaneousWasSelectedOnPreviousStep, IsProductSelected, ShowTankSelectionStep } from "../../utils/FormModelStoreTWSHelper";
import { StepsSlugs } from "./TWSProjectCalculationContainer";
import MatchingTapWaterSystemModel = Calculation.MatchingTapWaterSystemModel;
import WaterSystemOptionModel = Calculation.WaterSystemOptionModel;
import WaterSystemTechnicalDataModel = Calculation.WaterSystemTechnicalDataModel;
import MatchingUnitModel = Calculation.MatchingUnitModel;
import InputMethod = Calculation.InputMethod;
import SelectedWaterSystemPrimaryPressureErrors = Calculation.SelectedWaterSystemPrimaryPressureErrors;
import TWSPumpType = Calculation.TWSPumpType;
import ProductFamily = Selection.ProductFamily;

interface SelfState {
    matchingUnitModels: MatchingUnitModel[];
    capacityCalculation?: any;
    productCatalogCalculation?: any;
    matchingTapWaterSystemModels: MatchingTapWaterSystemModel[];
    matchingTapWaterSystemOptionModels: WaterSystemOptionModel[];
    matchingWaterSystemTechnicalDataModel: WaterSystemTechnicalDataModel | null;
    editModeLoaded: boolean;
}

export class WaterSystemStep extends BaseStep {
    _tWSCalculationApiService: TWSCalculationApiService | null = null;
    state: SelfState = {
        matchingUnitModels: [],
        matchingTapWaterSystemModels: [],
        matchingTapWaterSystemOptionModels: [],
        matchingWaterSystemTechnicalDataModel: null,
        editModeLoaded: false
    }

    constructor(props: any) {
        super(props);

        window.scheduleAtDocumentReady(() => {
            this._tWSCalculationApiService = APIServicesRegistry.getService(TWSCalculationApiService);
        })
    }

    protected override onEditMode(): void {
        if (this.state.editModeLoaded)
            return;

        super.onEditMode();

        this.setState({
            capacityCalculation: this.props.model.inputMethod.capacityCalculation,
            productCatalogCalculation: this.props.model.inputMethod.productCatalogCalculation,
            matchingUnitModels: this.props.model.inputMethod.results?.value.matchingUnitModels,
            matchingTapWaterSystemOptionModels: this.props.model.waterSystem.results?.value.matchingTapWaterSystemOptionModels,
            matchingWaterSystemTechnicalDataModel: this.props.model.waterSystem.results?.value.matchingWaterSystemTechnicalDataModel,
            matchingTapWaterSystemModels: this.props.model.waterSystem.results?.value.matchingTapWaterSystemModels,
            editModeLoaded: true //Water system step is in edit mode...
        });

        window.scheduleAtDocumentReady(() => {
            this.props.updateStepAvailability(StepsSlugs.TankSelection, ShowTankSelectionStep());

            this.props.stepChangeCallback(this.props.stepSlug, this.canProceed());
        });
    }

    static getStepModel(model: any): StepModel | null {
        return new StepModel(StepsSlugs.WaterSystem, model)
    }

    static getFields(step: StepModel) {
        const modifiedStep = { ...step, name: StepsSlugs.WaterSystem }
        const createField = (name: string, required: boolean = false, stepPart: string = StepsSlugs.WaterSystem) => {
            return new Field(name, { ...step, name: stepPart }, true, formModelStore, required)
        }
        return {
            // water system selection
            temperatureProgram: createField("waterSystemSelection.temperatureProgram"),
            primaryPump: createField("waterSystemSelection.primaryPump"),
            secondaryPump: createField("waterSystemSelection.secondaryPump"),
            controlValve: createField("waterSystemSelection.controlValve"),
            heType: createField("waterSystemSelection.heType"),
            // matching unit
            matchingTapWaterSystem: new TwsMatchingTapWaterSystemField("selectedTapWaterSystemModel", modifiedStep, true, formModelStore),
            // tws options
            twsOptions: new TwsTwsOptionsField("selectedTWSOptions", modifiedStep, true, formModelStore),
            matchingUnit: new TwsMatchingUnitField("installationTypeCalculation.selectedUnitModel", modifiedStep, true, formModelStore),
            tankFamily: createField("matchingTankUnit.tankFamily", false, StepsSlugs.TankSelection),
        }
    }

    getSecondaryPumpItems = () => {
        return [
            { value: TWSPumpType.No, text: __("No"), disabled: IsInstantaneousWasSelectedOnPreviousStep() === false },
            { value: TWSPumpType.Single, text: __("Single"), disabled: false },
            { value: TWSPumpType.Double, text: __("Double"), disabled: !IsInstantaneousWasSelectedOnPreviousStep() && this.fields.primaryPump.value == 1 },
        ]
    }

    render() {
        const selectedOptions = formModelStore.formModel.waterSystem?.selectedTWSOptions?.value ?? [];

        return (<React.Fragment>
            <div className={`generic-step-content`}>
                <div className={`row row-no-gutter`}>
                    <div className="column-6 column-xs-12">
                        <div className={`card`}>
                            <div className={`card-body`}>
                                <ExpandableContainer arrowFirst={true} label={__('System selection')}
                                    initialOpen={true}>
                                    <div className={`step-inputs-row`}>
                                        {this.renderField(this.fields.temperatureProgram)}
                                        {formModelStore.formModel.inputMethod.inputMethod.value === InputMethod.ProductCatalog && formModelStore.formModel.activeDirect === undefined && this.renderField(this.fields.tankFamily)}
                                    </div>
                                    <div className={`step-inputs-row`}>
                                        {this.renderField(this.fields.primaryPump)}
                                        {this.renderField(this.fields.controlValve)}
                                        {this.renderField(this.fields.secondaryPump, {
                                            disabled: IsInstantaneousWasSelectedOnPreviousStep(),
                                            items: this.getSecondaryPumpItems()
                                        })}
                                        {this.renderField(this.fields.heType)}
                                    </div>
                                </ExpandableContainer>
                                <ExpandableContainer arrowFirst={true}
                                    label={__('Matching tap water system(s)/ selection')}
                                    initialOpen={true} additionalClasses={['zero-padding']}>
                                    {this.renderField(this.fields.matchingTapWaterSystem, { additionalModel: this.state.matchingTapWaterSystemModels })}
                                </ExpandableContainer>
                                <ExpandableContainer arrowFirst={true} label={__('TWS option(s)')}
                                    initialOpen={true} additionalClasses={['zero-padding']}>
                                    {this.renderField(this.fields.twsOptions, { additionalModel: this.state.matchingTapWaterSystemOptionModels })}
                                </ExpandableContainer>
                            </div>
                        </div>
                    </div>
                    <div className={`column-6 column-xs-12`}>
                        <div className={`card`}>
                            <div className={`card-body`}>
                                <ExpandableContainer arrowFirst={true} label={__('General info')} initialOpen={true}
                                    additionalClasses={[formModelStore.formModel.inputMethod.inputMethod.value === InputMethod.InstallationType ? 'zero-padding' : '']}>
                                    {this.renderInstallationTypeCalculationGeneralInfo()}
                                    {this.renderCapacityCalculationGeneralInfo()}
                                    {this.renderProductCatalogCalculationGeneralInfo()}
                                </ExpandableContainer>
                                <ExpandableContainer arrowFirst={true} label={__('TWS technical data')}
                                    initialOpen={true}>
                                    {this.state.matchingWaterSystemTechnicalDataModel?.productFamily &&
                                        <WaterSystemTechnicalData {...this.state.matchingWaterSystemTechnicalDataModel} optionsWeight={GetOptionsWeight(selectedOptions)} />}
                                </ExpandableContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>)
    }

    renderInstallationTypeCalculationGeneralInfo = () => {
        if (formModelStore.formModel.inputMethod.inputMethod.value !== InputMethod.InstallationType) {
            return <div></div>
        }
        return this.renderField(this.fields.matchingUnit, {
            additionalModel: this.state.matchingUnitModels,
            readonly: true
        });
    }

    renderCapacityCalculationGeneralInfo = () => {
        if (formModelStore.formModel.inputMethod.inputMethod.value !== InputMethod.Capacity) {
            return <div></div>
        }
        return <div className="step-inputs-row grid-2">
            {RenderStepInputReadonlyField("Capacity", this.state.capacityCalculation?.capacity?.value, "kW")}
            {(this.state.capacityCalculation?.primaryTank?.value || this.state.capacityCalculation?.semiInstantaneous?.value) && RenderStepInputReadonlyField("Volume", this.state.capacityCalculation?.volume?.value, "L")}
            {RenderStepInputReadonlyField("Instantaneous", this.state.capacityCalculation?.instantaneous?.value ? __("Yes") : __("No"))}
            {RenderStepInputReadonlyField("Primary tank", this.state.capacityCalculation?.primaryTank?.value ? __("Yes") : __("No"))}
            {RenderStepInputReadonlyField("Semi-instantaneous", this.state.capacityCalculation?.semiInstantaneous?.value ? __("Yes") : __("No"))}
        </div>
    }

    renderProductCatalogCalculationGeneralInfo = () => {
        if (formModelStore.formModel.inputMethod.inputMethod.value !== InputMethod.ProductCatalog) {
            return <div></div>
        }
        return <div className="step-inputs-row" style={{ height: 200 }}>
            <div className={`tws-product-family`}>
                <div className={`description`}>
                    <div className={`name`}>
                        {this.state.productCatalogCalculation?.selectedProduct?.value?.displayName}
                        <div className={`title`}>
                            {__(this.state.productCatalogCalculation?.selectedProduct?.value?.title)}
                        </div>
                    </div>
                    {TwsProductCatalogSelectProduct.renderInformationSection(this.state.productCatalogCalculation?.selectedProduct?.value)}
                </div>
                {this.state.productCatalogCalculation?.selectedProduct?.value?.image && <div className={`image`} style={{ backgroundImage: `url(${this.state.productCatalogCalculation?.selectedProduct?.value?.image})` }} />}
            </div>
        </div>
    }

    runDisableLogic = () => {
        if (formModelStore.recentlyChangedField === this.fields.controlValve.getModelPath()) {
            if ([1, 2, 4].indexOf(this.fields.controlValve.value) > -1) {
                this.fields.primaryPump.setValue(0);
            } else {
                this.fields.primaryPump.setValue(1);
            }
        }
        if (formModelStore.recentlyChangedField === this.fields.primaryPump.getModelPath()) {
            if (this.fields.primaryPump.value === 0 || this.fields.primaryPump.value === 1) {
                this.fields.controlValve.setValue(0);
            }
            if (!IsInstantaneousWasSelectedOnPreviousStep()) {
                if (this.fields.primaryPump.value === 1) {
                    this.fields.secondaryPump.setValue(1);
                }
                if (this.fields.primaryPump.value === 2) {
                    this.fields.secondaryPump.setValue(2);
                }
            }
        }
    }

    onFormModelChange() {
        formModelStore.recentlyChangedFormSection = this.props.stepSlug;
        this.runDisableLogic();
        this.props.setLastAvailableStep(this.props.stepSlug);
        this.cancelSelectionsFromModelStore();
        if (formModelStore.recentlyChangedField === this.fields.matchingTapWaterSystem.getModelPath()) {
            this.calculateSelectedTapWaterData();
        } else if (formModelStore.recentlyChangedField !== this.fields.twsOptions.getModelPath()) {
            this.calculateMatchingTapWaterSystems()
        } else {
            this.calculateSelectedTapWaterData();
            this.props.stepChangeCallback(this.props.stepSlug, this.canProceed());
            formModelStore.formModel.waterSystem.results = {
                matchingTapWaterSystemOptionModels: this.state.matchingTapWaterSystemOptionModels,
                matchingWaterSystemTechnicalDataModel: this.state.matchingWaterSystemTechnicalDataModel,
                matchingTapWaterSystemModels: this.state.matchingTapWaterSystemModels
            };
        }
        this.props.updateStepAvailability(StepsSlugs.TankSelection, ShowTankSelectionStep());
    }

    calculateMatchingTapWaterSystems = () => {
        this._tWSCalculationApiService?.calculateMatchingTapWaterSystems(flattenConfigurationModel(formModelStore).formModel)
            .then(response => {
                this.setState({
                    matchingTapWaterSystemModels: response.data,
                    matchingTapWaterSystemOptionModels: [],
                    matchingWaterSystemTechnicalDataModel: {},
                })
                formModelStore.formModel.waterSystem.selectedTapWaterSystemModel = {}
                formModelStore.formModel.waterSystem.selectedTWSOptions = []
                this.props.stepChangeCallback(this.props.stepSlug, false);
                this.props.setDirty()
            })
    }
    calculateSelectedTapWaterData = () => {
        this._tWSCalculationApiService?.calculateSelectedTapWaterData(flattenConfigurationModel(formModelStore).formModel)
            .then(response => {
                this.setState({
                    matchingTapWaterSystemOptionModels: response.data.matchingTapWaterSystemOptionModels,
                    matchingWaterSystemTechnicalDataModel: response.data.matchingWaterSystemTechnicalDataModel
                })
                //TODO: DECIDE WHICH PLACE IS MORE APPRPPRIATE / ...THROW AWAY ONE OF THEM BEFORE KICKOFF
                formModelStore.formModel.waterSystem.results.value = {
                    matchingTapWaterSystemOptionModels: response.data.matchingTapWaterSystemOptionModels,
                    matchingWaterSystemTechnicalDataModel: response.data.matchingWaterSystemTechnicalDataModel,
                    matchingTapWaterSystemModels: this.state.matchingTapWaterSystemModels
                };
                this.props.stepChangeCallback(this.props.stepSlug, this.canProceed());
                this.props.setDirty()
            })
    }

    updateDefaults(prevProps: Readonly<any>) {
        if (!prevProps.visible && this.props.visible) {
            if (IsProductSelected(ProductFamily.AquaPlate)) {
                this.fields.primaryPump.setValue(TWSPumpType.No);
                this.fields.secondaryPump.setValue(TWSPumpType.No);
            }
            else if (IsInstantaneousWasSelectedOnPreviousStep() !== false) {
                this.fields.secondaryPump.setValue(TWSPumpType.No);
            } else {
                this.fields.secondaryPump.setValue(TWSPumpType.Single);
            }
        }
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any) {
        this.updateDefaults(prevProps);

        if (!prevProps.visible && this.props.visible && !Object.values(formModelStore.formModel.waterSystem.selectedTapWaterSystemModel).length) {
            this.props.setLastAvailableStep(this.props.stepSlug);
            if (formModelStore.formModel.inputMethod.inputMethod.value === InputMethod.ProductCatalog &&
                formModelStore.formModel.inputMethod?.productCatalogCalculation?.temperatureProgram?.value) {
                this.fields.temperatureProgram.setValue(formModelStore.formModel.inputMethod?.productCatalogCalculation?.temperatureProgram?.value);
            }
            formModelStore.formModel.tankSelection.selectedMatchingTankQuantity.value = null;
            formModelStore.formModel.tankSelection.selectedTank.value = null;
            this.calculateMatchingTapWaterSystems();
            this.setState({
                matchingUnitModels: formModelStore.formModel.inputMethod?.results?.value.matchingUnitModels,
                capacityCalculation: formModelStore.formModel.inputMethod?.capacityCalculation,
                productCatalogCalculation: formModelStore.formModel.inputMethod?.productCatalogCalculation
            })
            this.props.stepChangeCallback(this.props.stepSlug, this.canProceed());
        }
        if (!prevProps.visible && this.props.visible && Object.values(formModelStore.formModel.waterSystem.selectedTapWaterSystemModel).length) {
            this.props.stepChangeCallback(this.props.stepSlug, this.canProceed());
        }
    }


    canProceed = () => {
        return this.fields.matchingTapWaterSystem.value && this.state.matchingWaterSystemTechnicalDataModel?.primaryPumpModel.freePressureError == SelectedWaterSystemPrimaryPressureErrors.NoError;
    }

    private cancelSelectionsFromModelStore() {
        formModelStore.formModel.tankSelection.selectedMatchingTankQuantity.value = null;
        formModelStore.formModel.tankSelection.selectedTank.value = null;

        if (formModelStore.recentlyChangedField === this.fields.matchingTapWaterSystem.getModelPath()) {
            this.fields.twsOptions.ref.clear();
            return;
        }

        if (formModelStore.recentlyChangedField === this.fields.twsOptions.getModelPath()) {
            return;
        }

        formModelStore.formModel.waterSystem.selectedTapWaterSystemModel.value = null
        this.props.stepChangeCallback(this.props.stepSlug, false);
    }
}

